/**
 * Header of the curve editor column (W2-04b curve-editor; DESIGN.md §4.7 wireframe).
 *
 * Two rows inside the host's header slot (52 px):
 *  1. property (or Guided pair: "Position X and Y") and line, layer chips (Full, layered properties; labelled states:
 *     filled dot = has keys, hollow = empty, crossed eye = muted in the preview), add layer, the Σ / ghosts / channel
 *     view / distance toggles, value axis fit and zoom, close;
 *  2. single view: the selection summary and the segment actions ("Change curve…", "Stay, then jump", "Make jump",
 *     "Continue from previous", "Turn into a custom curve", "Use the whole curve", "Bake to steps…", "Reverse",
 *     "Link"), each disabled with the precise reason (no segment, no key with a jump, keys that already jump, locked
 *     line); channel view: one label per lane with the read-only Σ and "Flatten to layer 0" (ChannelView.tsx).
 * Every control is a lucide icon button with an accessible label and tooltip; the rows are toolbars (one tab stop,
 * arrow keys inside) and never take focus on pointer down.
 */

import { useEffect, useReducer, type ComponentType } from 'react';
import type { AppServices } from '../../app/registry/types';
import type { MessageRef } from '../../core/util/types';
import { Icon, IconButton, Toolbar } from '../../ui';
import {
  addLayer,
  cannotAddLayer,
  cannotEditSegments,
  cannotMakeBezier,
  cannotMakeJump,
  cannotRemoveJump,
  changeCurve,
  collapseEditor,
  makeBezier,
  makeJump,
  removeJump,
  resetSlice,
  selectedKeys,
  selectedSegments,
  slicedSegments,
  switchLayer,
  toggleHold,
} from './curveActions';
import type { CurveView } from './curveColumn';
import { m } from './curveEditor.messages';
import s from './CurveEditor.module.css';
import { ChannelView } from './ChannelView';
import { isLayered, layerCount, layerLabel, lineLabel, pairLabel, propLabel, unitContextOf } from './curveModel';
import { readCurveSetting, toggleCurveSetting, type CurveSettingKey } from './curveSettings';

/** Re-renders on view changes (scene, selection, settings, language) and on service swaps. */
function useLive(view: CurveView, services: AppServices): void {
  const [, bump] = useReducer((x: number) => x + 1, 0);
  useEffect(() => {
    const a = view.subscribe(bump);
    const b = services.onChange(bump);
    return () => {
      a();
      b();
    };
  }, [view, services]);
}

function relayout(services: AppServices): void {
  services.timeline?.relayout();
}

function SettingToggle(props: {
  readonly services: AppServices;
  readonly setting: CurveSettingKey;
  readonly icon: string;
  readonly label: MessageRef;
  readonly command: string;
  readonly disabledReason?: MessageRef | null;
  readonly relayout?: boolean;
}) {
  const on = readCurveSetting(props.services.settings, props.setting);
  return (
    <IconButton
      size="sm"
      icon={props.icon}
      label={props.label}
      command={props.command}
      pressed={on}
      disabledReason={props.disabledReason ?? null}
      onClick={() => {
        toggleCurveSetting(props.services.settings, props.setting);
        if (props.relayout === true) relayout(props.services);
      }}
    />
  );
}

function SelectionBar(props: { readonly services: AppServices; readonly editable: boolean }) {
  const { services } = props;
  const segs = selectedSegments(services);
  const keys = selectedKeys(services);
  const tr = (ref: MessageRef): string => services.i18n.tr(ref);
  const summary: MessageRef =
    segs.length > 0 && keys.length > 0
      ? { key: m.selectionBoth, params: { keys: keys.length, segments: segs.length } }
      : segs.length > 0
        ? { key: m.selectionSegments, params: { count: segs.length } }
        : keys.length > 0
          ? { key: m.selectionKeys, params: { count: keys.length } }
          : { key: m.selectionNone };
  const locked: MessageRef | null = props.editable ? null : { key: m.lineLocked };
  const noSegs: MessageRef | null = locked ?? cannotEditSegments(services);
  const sliced = slicedSegments(services);
  return (
    <Toolbar label={{ key: m.actionsLabel }}>
      <span className={s.summary} aria-live="polite" title={tr(summary)}>
        {tr(summary)}
      </span>
      <IconButton
        size="sm"
        icon="Spline"
        label={{ key: m.btnChangeCurve }}
        disabledReason={noSegs}
        onClick={() => {
          void changeCurve(services, selectedSegments(services));
        }}
      />
      <IconButton
        size="sm"
        icon="StepForward"
        label={{ key: m.btnHold }}
        disabledReason={noSegs}
        onClick={() => {
          toggleHold(services);
        }}
      />
      <IconButton
        size="sm"
        icon="ArrowUpFromLine"
        label={{ key: m.btnJump }}
        disabledReason={locked ?? cannotMakeJump(services)}
        onClick={() => {
          makeJump(services);
        }}
      />
      <IconButton
        size="sm"
        icon="CornerDownRight"
        label={{ key: m.btnRemoveJump }}
        disabledReason={locked ?? cannotRemoveJump(services)}
        onClick={() => {
          removeJump(services);
        }}
      />
      <IconButton
        size="sm"
        icon="PenTool"
        label={{ key: m.cmdMakeBezier }}
        command="curve.makeBezier"
        disabledReason={locked ?? cannotMakeBezier(services)}
        onClick={() => {
          makeBezier(services);
        }}
      />
      <IconButton
        size="sm"
        icon="Maximize2"
        label={{ key: m.cmdResetSlice }}
        command="curve.resetSlice"
        disabledReason={locked ?? (sliced.length === 0 ? { key: m.noSlicedSegments } : null)}
        onClick={() => {
          resetSlice(services);
        }}
      />
      <IconButton
        size="sm"
        icon="Grid3x3"
        label={{ key: m.btnBake }}
        disabledReason={noSegs}
        onClick={() => {
          void services.registry.run('segments.bake', undefined);
        }}
      />
      <IconButton
        size="sm"
        icon="ArrowUpDown"
        label={{ key: m.btnReverse }}
        disabledReason={noSegs}
        onClick={() => {
          void services.registry.run('segments.reverse', undefined);
        }}
      />
      <IconButton
        size="sm"
        icon="Link"
        label={{ key: m.btnLink }}
        disabledReason={noSegs}
        onClick={() => {
          void services.registry.run('segments.link', undefined);
        }}
      />
    </Toolbar>
  );
}

/** Builds the header component of one curve editor instance. */
export function createCurveHeader(
  view: CurveView,
  services: AppServices,
): ComponentType<{ readonly instanceId: string }> {
  function CurveHeader(_props: { readonly instanceId: string }) {
    useLive(view, services);
    const store = services.store;
    const { target } = view;
    if (store === null || !store.doc.lines.has(target.line)) return null;
    const st = services.editor.getState();
    const i18n = services.i18n;
    const ctx = unitContextOf(store.doc, store.idx, target.line);
    const prop = view.pair === null ? propLabel(target.prop, ctx, i18n) : pairLabel(view.pair, i18n);
    const line = lineLabel(store.doc, store.idx, target.line, i18n);
    const layered = isLayered(target.prop);
    const full = st.level === 'full';
    const count = layerCount(store.doc, store.idx, target.line, target.prop);
    // The Σ and ghost overlays apply when a shown property has several layers (either lane of a pair).
    const overlayLayers = Math.max(
      ...(view.pair ?? [target.prop]).map((p) => layerCount(store.doc, store.idx, target.line, p)),
    );
    const muted = st.filters.mutedLayers.get(target.line);
    const editable = !st.filters.lockedLines.has(target.line);
    const channelReason: MessageRef | null = !layered
      ? { key: m.channelNeedsLayers }
      : !full
        ? { key: m.channelFullOnly }
        : null;
    const overlayReason: MessageRef | null = view.channel
      ? { key: m.channelShowsAll }
      : layered && overlayLayers > 1
        ? null
        : { key: m.onlyOneLayer };

    return (
      <div
        className={s.header}
        data-region="curve"
        data-context-menu="curve.context"
        data-help-id="curve-editor"
        aria-label={i18n.t(m.headerRegion, { prop, line })}
      >
        <Toolbar label={{ key: m.toolbarLabel }}>
          <span className={s.title} title={i18n.t(m.headerTitle, { prop, line })}>
            <Icon name="ChartSpline" size={14} />
            <span className={s.titleText}>{i18n.t(m.headerTitle, { prop, line })}</span>
          </span>
          {layered && full && !view.channel && view.pair === null ? (
            <span className={s.chips} role="group" aria-label={i18n.t(m.layersLabel)}>
              {Array.from({ length: count }, (_, l) => {
                const name = layerLabel(store.doc, target.line, l, i18n);
                const has = store.idx.track(target.line, target.prop, l) !== undefined;
                const isMuted = muted?.has(`${target.prop}:${String(l)}`) === true;
                const hint = i18n.t(m.layerChipHint, {
                  name,
                  state: has ? 'keys' : 'empty',
                  muted: isMuted ? 'yes' : 'no',
                });
                return (
                  <button
                    key={l}
                    type="button"
                    className={s.chip}
                    aria-pressed={l === target.layer}
                    aria-label={hint}
                    title={hint}
                    onMouseDown={(e) => {
                      e.preventDefault();
                    }}
                    onClick={() => {
                      switchLayer(services, l);
                    }}
                  >
                    <Icon name={has ? 'CircleDot' : 'Circle'} size={12} />
                    <span>{name}</span>
                    {isMuted ? <Icon name="EyeOff" size={12} label={{ key: m.layerMuted }} /> : null}
                  </button>
                );
              })}
              <IconButton
                size="sm"
                icon="Plus"
                label={{ key: m.btnAddLayer }}
                command="curve.addLayer"
                disabledReason={editable ? cannotAddLayer(services, target) : { key: m.lineLocked }}
                onClick={() => {
                  addLayer(services, target);
                }}
              />
            </span>
          ) : null}
          <span className={s.spacer} />
          {layered ? (
            <>
              <SettingToggle
                services={services}
                setting="sigma"
                icon="Sigma"
                label={{ key: m.cmdSigma }}
                command="curve.sigma"
                disabledReason={overlayReason}
              />
              <SettingToggle
                services={services}
                setting="ghosts"
                icon="Ghost"
                label={{ key: m.cmdGhosts }}
                command="curve.ghosts"
                disabledReason={overlayReason}
              />
              <SettingToggle
                services={services}
                setting="channel"
                icon="Columns3"
                label={{ key: m.cmdChannelView }}
                command="curve.channelView"
                disabledReason={channelReason}
                relayout
              />
            </>
          ) : null}
          {target.prop === 'speed' ? (
            <SettingToggle
              services={services}
              setting="distance"
              icon="Ruler"
              label={{ key: m.cmdDistance }}
              command="curve.distance"
            />
          ) : null}
          <IconButton
            size="sm"
            icon="Scan"
            label={{ key: m.cmdFitValues }}
            command="curve.fitValues"
            onClick={() => {
              view.fitValues();
            }}
          />
          <IconButton
            size="sm"
            icon="ZoomIn"
            label={{ key: m.cmdZoomValuesIn }}
            command="curve.zoomValuesIn"
            onClick={() => {
              view.zoomValues(1.5);
            }}
          />
          <IconButton
            size="sm"
            icon="ZoomOut"
            label={{ key: m.cmdZoomValuesOut }}
            command="curve.zoomValuesOut"
            onClick={() => {
              view.zoomValues(1 / 1.5);
            }}
          />
          <IconButton
            size="sm"
            icon="X"
            label={{ key: m.cmdCollapse }}
            command="curve.collapse"
            onClick={() => {
              collapseEditor(services);
            }}
          />
        </Toolbar>
        {view.channel ? (
          <ChannelView services={services} view={view} />
        ) : (
          <SelectionBar services={services} editable={editable} />
        )}
      </div>
    );
  }
  return CurveHeader;
}
